import React, { useState, useEffect, useRef } from 'react';
import { FaWhatsapp, FaInstagram, FaLeaf, FaHeart, FaShieldAlt, FaBars, FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt, FaStar, FaQuoteLeft, FaArrowUp, FaTiktok } from 'react-icons/fa';

// Componente de Navegación
const Navigation = ({ isMenuOpen, setIsMenuOpen, scrolled, customBuyBtn }) => {
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/90 backdrop-blur-lg shadow-lg' : 'bg-black/80 backdrop-blur-md'
    } border-b border-teal-400/20`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/LogoFinal.png"
              alt="Logo Té Ñawi"
              className="w-12 h-12 rounded-full object-cover shadow-lg"
            />
            <span className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
              Té Ñawi
            </span>
          </div>
          
          <ul className="hidden lg:flex gap-8">
            {['Inicio', 'Beneficios', 'Producto', 'Testimonios', 'Contacto'].map((item) => (
              <li key={item}>
                <a 
                  href={`#${item.toLowerCase().replace('í', 'i')}`} 
                  className="text-gray-300 hover:text-teal-400 transition-all duration-300 font-medium relative group"
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-teal-400 to-amber-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4">
            {customBuyBtn ? customBuyBtn : (
              <a
                href="https://wa.me/59168759357"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-all duration-300 shadow-lg items-center gap-2"
              >
                <FaWhatsapp />
                Pedir Ahora
              </a>
            )}
            
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-2xl text-teal-400 hover:text-amber-400 transition-colors"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="py-4 space-y-4">
            {['Inicio', 'Beneficios', 'Producto', 'Testimonios', 'Contacto'].map((item) => (
              <a 
                key={item}
                href={`#${item.toLowerCase().replace('í', 'i')}`} 
                className="block text-gray-300 hover:text-teal-400 transition-colors py-2 border-b border-gray-800"
                onClick={() => setIsMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <a
              href="https://wa.me/59168759357"
              target="_blank"
              rel="noopener noreferrer"
              className="flex bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-full font-semibold items-center gap-2 justify-center mt-4"
            >
              <FaWhatsapp />
              Pedir Ahora
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

// Componente Hero
const HeroSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = [
    { text: "Increíble sabor y me siento mucho mejor desde que lo tomo", author: "María C." },
    { text: "100% recomendado, es mi té favorito ahora", author: "Carlos R." },
    { text: "Perfecto para relajarse después del trabajo", author: "Ana L." }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-teal-900/20 via-black to-amber-900/20"></div>
        <div className="absolute top-20 left-20 w-32 h-32 bg-teal-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <svg className="absolute bottom-0 left-0 w-full h-64" viewBox="0 0 1200 320" preserveAspectRatio="none">
          <path 
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,106.7C1248,96,1344,96,1392,96L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" 
            fill="url(#heroGradient)"
            fillOpacity="0.3"
          />
          <defs>
            <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2dd4bf" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500/20 to-amber-500/20 backdrop-blur-sm border border-teal-400/30 rounded-full px-4 py-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-300">Disponible ahora</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="block bg-gradient-to-r from-teal-400 via-green-400 to-amber-400 bg-clip-text text-transparent animate-gradient">
                  Té Ñawi
                </span>
                <span className="block text-2xl lg:text-3xl text-gray-300 font-normal mt-4">
                  Sabiduría Ancestral
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-lg">
                Infusión ancestral de zanahoria que conecta tu cuerpo con la sabiduría de la Pachamama
              </p>
            </div>

            {/* Testimonial Carousel */}
            <div className="bg-gradient-to-r from-teal-500/10 to-amber-500/10 backdrop-blur-sm border border-teal-400/20 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <FaQuoteLeft className="text-teal-400 text-2xl" />
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-amber-400 text-sm" />
                  ))}
                </div>
              </div>
              <p className="text-gray-300 italic mb-3">"{testimonials[currentTestimonial].text}"</p>
              <p className="text-teal-400 font-semibold">- {testimonials[currentTestimonial].author}</p>
            </div>

            {/* Price & CTA */}
            <div className="space-y-6">
            

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/59168759357"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  <FaWhatsapp className="text-xl" />
                  Pedir por WhatsApp
                </a>
                <a
                  href="#beneficios"
                  className="px-8 py-4 border border-teal-400/50 rounded-full text-teal-400 hover:bg-teal-400/10 transition-all duration-300 text-center"
                >
                  Ver Beneficios
                </a>
              </div>
            </div>
          </div>

          {/* Right Product Display */}
          <div className="relative z-10 bg-gradient-to-br from-teal-500/10 to-amber-500/10 backdrop-blur-sm border border-teal-400/20 rounded-3xl p-8 hover:scale-105 transition-all duration-500">
            <div className="bg-gradient-to-br from-amber-50 to-teal-50 rounded-2xl p-8 text-center shadow-2xl">
              <ProductCarousel />
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Té Ñawi</h3>
              <p className="text-gray-600 mb-4">Infusión Premium de Zanahoria</p>
              
              
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Componente de Beneficios
const BenefitsSection = () => {
  const benefits = [
    {
      icon: FaShieldAlt,
      title: "Sistema Inmunológico",
      description: "Rico en antioxidantes y vitamina A, fortalece tus defensas naturales como lo hacían nuestros abuelos.",
      gradient: "from-teal-400 to-green-500",
      bgGradient: "from-teal-500/10 to-green-500/10",
      borderColor: "border-teal-400/20"
    },
    {
      icon: FaHeart,
      title: "Digestión Saludable",
      description: "Mejora tu digestión de forma natural, conectando tu cuerpo with el equilibrio de la Pachamama.",
      gradient: "from-amber-400 to-orange-500",
      bgGradient: "from-amber-500/10 to-orange-500/10",
      borderColor: "border-amber-400/20"
    },
    {
      icon: FaLeaf,
      title: "100% Natural",
      description: "Sin aditivos, sin cafeína. Pura zanahoria deshidratada con amor y respeto por la naturaleza.",
      gradient: "from-green-500 to-teal-400",
      bgGradient: "from-green-500/10 to-teal-500/10",
      borderColor: "border-green-400/20"
    }
  ];

  return (
    <section id="beneficios" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
            Beneficios Ancestrales
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Redescubre los poderes curativos de la zanahoria en cada sorbo, honrando la tradición de nuestros ancestros
            que conocían los secretos de la naturaleza.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div 
                key={index}
                className={`group bg-gradient-to-br ${benefit.bgGradient} backdrop-blur-sm border ${benefit.borderColor} rounded-2xl p-8 hover:scale-105 hover:shadow-2xl transition-all duration-500`}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${benefit.gradient} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                  <IconComponent className="text-3xl text-black" />
                </div>
                <h3 className="text-2xl font-bold text-teal-400 mb-4">{benefit.title}</h3>
                <p className="text-gray-300 leading-relaxed text-lg">{benefit.description}</p>
              </div>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { number: "100%", label: "Natural" },
            { number: "0", label: "Aditivos" },
            { number: "500+", label: "Clientes Felices" },
            { number: "24h", label: "Entrega Rápida" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Componente de Producto
const ProductSection = () => {
  return (
    <section id="producto" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0">
        <svg className="absolute top-0 left-0 w-full h-64" viewBox="0 0 1200 320" preserveAspectRatio="none">
          <path 
            d="M0,224L48,208C96,192,192,160,288,160C384,160,480,192,576,197.3C672,203,768,181,864,181.3C960,181,1056,203,1152,213.3C1248,224,1344,224,1392,224L1440,224L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" 
            fill="url(#productGradient)"
            fillOpacity="0.1"
          />
          <defs>
            <linearGradient id="productGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#2dd4bf" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="bg-gradient-to-br from-teal-500/10 to-amber-500/10 backdrop-blur-sm border border-teal-400/20 rounded-3xl p-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
              Ingredientes Sagrados
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Cada ingrediente es seleccionado cuidadosamente siguiendo tradiciones ancestrales bolivianas
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {[
                {
                  color: "teal-400",
                  title: "100% zanahoria deshidratada",
                  description: "Cultivada en suelos bolivianos con técnicas ancestrales transmitidas de generación en generación"
                },
                {
                  color: "amber-400", 
                  title: "Sin aditivos químicos",
                  description: "Respetamos la pureza que nos enseñó la Pachamama, sin conservantes ni colorantes artificiales"
                },
                {
                  color: "green-400",
                  title: "Libre de cafeína",
                  description: "Perfecto para cualquier momento del día, sin alterar tu ciclo natural de sueño"
                },
                {
                  color: "orange-400",
                  title: "Producción ecológica",
                  description: "Cuidamos la tierra como ella nos cuida a nosotros, con prácticas sostenibles"
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className={`w-4 h-4 bg-${item.color} rounded-full mt-2 flex-shrink-0 group-hover:scale-125 transition-transform`}></div>
                  <div>
                    <p className="text-lg text-gray-300">
                      <span className={`font-semibold text-${item.color}`}>{item.title}:</span> {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-400/20 rounded-2xl p-8 text-center hover:scale-105 transition-all duration-500">
              <div className="text-8xl mb-6">🥕</div>
              <h3 className="text-3xl font-bold text-amber-400 mb-4">Origen Local</h3>
              <p className="text-gray-300 leading-relaxed text-lg mb-6">
                Cultivado en las tierras fértiles de Bolivia con el amor y respeto de nuestros productores locales
              </p>
              <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-black px-6 py-3 rounded-full font-bold inline-block">
                Certificado Orgánico
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reemplazar el título y contenido de la sección de testimonios por formas de preparación
const PreparationSection = () => {
  const steps = [
    {
      title: "Calienta el agua",
      description: "Lleva el agua a punto de ebullición (aprox. 90-95°C). Usa agua filtrada para mejor sabor.",
      icon: <span role="img" aria-label="agua">💧</span>
    },
    {
      title: "Agrega el té",
      description: "Coloca 1-2 cucharaditas de Té Ñawi por taza (250ml) en un infusor o directamente en la taza.",
      icon: <span role="img" aria-label="cucharadita">🥄</span>
    },
    {
      title: "Vierte el agua",
      description: "Vierte el agua caliente sobre el té y deja reposar de 5 a 7 minutos.",
      icon: <span role="img" aria-label="verter">☕</span>
    },
    {
      title: "Disfruta",
      description: "Puedes endulzar con miel o stevia al gusto. ¡Disfruta tu infusión ancestral!",
      icon: <span role="img" aria-label="disfrutar">🍯</span>
    }
  ];

  return (
    <section id="preparacion" className="py-20 relative bg-black/80">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
            Formas de preparación
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Sigue estos pasos para preparar la mejor infusión de Té Ñawi y aprovechar todos sus beneficios.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="bg-gradient-to-br from-teal-500/10 to-amber-500/10 border border-teal-400/20 rounded-2xl p-8 text-center flex flex-col items-center hover:scale-105 transition-all duration-500">
              <div className="text-5xl mb-4">{step.icon}</div>
              <h3 className="text-xl font-bold text-teal-400 mb-2">{step.title}</h3>
              <p className="text-gray-300 text-base">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Carrusel de imágenes para el producto en HeroSection
const ProductCarousel = () => {
  const images = [
    '/Producto.jpg',
    '/Producto2.jpg',
    '/Producto3.jpg',
    '/Producto4.jpg',
  ];
  const [current, setCurrent] = useState(0);

  const prevImage = () => setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const nextImage = () => setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  return (
    <div className="relative w-[350px] h-[350px] mx-auto mb-6">
      <img
        src={images[current]}
        alt={`Producto Té Ñawi ${current + 1}`}
        className="w-full h-full object-cover rounded-3xl border-4 border-white shadow-2xl transition-all duration-300"
      />
      {/* Flechas */}
      <button
        onClick={prevImage}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-teal-500 transition-colors"
        aria-label="Anterior"
      >
        &#8592;
      </button>
      <button
        onClick={nextImage}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 text-white rounded-full p-2 hover:bg-teal-500 transition-colors"
        aria-label="Siguiente"
      >
        &#8594;
      </button>
    </div>
  );
};

// Modal de login/registro
const AuthModal = ({ open, onClose, onLogin, onRegister, error }) => {
  const [tab, setTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ nombre: '', apellido: '', telefono: '', email: '', password: '' });

  useEffect(() => {
    if (!open) {
      setTab('login');
      setLoginData({ email: '', password: '' });
      setRegisterData({ nombre: '', apellido: '', telefono: '', email: '', password: '' });
    }
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-2xl">×</button>
        <div className="flex mb-6">
          <button
            className={`flex-1 py-2 font-bold rounded-l-2xl ${tab === 'login' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setTab('login')}
          >Iniciar sesión</button>
          <button
            className={`flex-1 py-2 font-bold rounded-r-2xl ${tab === 'register' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setTab('register')}
          >Registrarse</button>
        </div>
        {tab === 'login' ? (
          <form onSubmit={e => { e.preventDefault(); onLogin(loginData); }}>
            <input
              type="email"
              placeholder="Correo electrónico"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={loginData.email}
              onChange={e => setLoginData({ ...loginData, email: e.target.value })}
              required
            />
            <input
              type="password"
              placeholder="Contraseña"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={loginData.password}
              onChange={e => setLoginData({ ...loginData, password: e.target.value })}
              required
            />
            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
            <button type="submit" className="w-full bg-teal-500 text-white py-2 rounded font-bold hover:bg-teal-600 transition">Entrar</button>
          </form>
        ) : (
          <form onSubmit={e => { e.preventDefault(); onRegister(registerData); }}>
            <input
              type="text"
              placeholder="Nombre"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={registerData.nombre}
              onChange={e => setRegisterData({ ...registerData, nombre: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Apellido"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={registerData.apellido}
              onChange={e => setRegisterData({ ...registerData, apellido: e.target.value })}
              required
            />
            <input
              type="tel"
              placeholder="Teléfono"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={registerData.telefono}
              onChange={e => setRegisterData({ ...registerData, telefono: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder="Correo electrónico"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={registerData.email}
              onChange={e => setRegisterData({ ...registerData, email: e.target.value })}
              required
            />
            <input
              type="password"
              placeholder="Contraseña"
              className="w-full mb-3 px-4 py-2 border rounded"
              value={registerData.password}
              onChange={e => setRegisterData({ ...registerData, password: e.target.value })}
              required
            />
            {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
            <button type="submit" className="w-full bg-teal-500 text-white py-2 rounded font-bold hover:bg-teal-600 transition">Registrarse</button>
          </form>
        )}
      </div>
    </div>
  );
};

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [users, setUsers] = useState([]); // [{email, password, compras: []}]
  const [user, setUser] = useState(null); // usuario logueado
  const [authError, setAuthError] = useState('');
  const [pendingBuy, setPendingBuy] = useState(null); // producto pendiente de compra

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cambiar funciones de login/registro para validar correctamente
  const handleLogin = ({ email, password }) => {
    const found = users.find(u => u.email === email && u.password === password);
    if (!users.find(u => u.email === email)) {
      setAuthError('El usuario no está registrado. Por favor regístrate primero.');
      return;
    }
    if (found) {
      setUser(found);
      setAuthOpen(false);
      setAuthError('');
      if (pendingBuy) {
        found.compras = found.compras || [];
        found.compras.push(pendingBuy);
        setUsers([...users]);
        setPendingBuy(null);
        alert('¡Compra registrada para ' + found.email + '!');
      }
    } else {
      setAuthError('Contraseña incorrecta');
    }
  };
  const handleRegister = (data) => {
    if (!data.nombre || !data.apellido || !data.telefono || !data.email || !data.password) {
      setAuthError('Por favor completa todos los campos');
      return;
    }
    if (users.find(u => u.email === data.email)) {
      setAuthError('El correo ya está registrado');
      return;
    }
    const newUser = { ...data, compras: [] };
    setUsers([...users, newUser]);
    setUser(newUser);
    setAuthOpen(false);
    setAuthError('');
    if (pendingBuy) {
      newUser.compras.push(pendingBuy);
      setUsers([...users, newUser]);
      setPendingBuy(null);
      alert('¡Compra registrada para ' + newUser.email + '!');
    }
  };

  // Cambiar botón del header
  const handleBuyClick = () => {
    setPendingBuy({ nombre: 'Té Ñawi', imagen: '/Producto.jpg' });
    setAuthOpen(true);
  };

  return (
    <div className="bg-black min-h-screen text-white font-sans">
      {/* Header con botón de compra */}
      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrolled={scrolled} customBuyBtn={
        <button
          onClick={handleBuyClick}
          className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-2 rounded-full font-semibold hover:scale-105 transition-transform"
        >
          Comprar ahora
        </button>
      } />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onLogin={handleLogin} onRegister={handleRegister} error={authError} />
      <HeroSection />
      <BenefitsSection />
      <ProductSection />
      <PreparationSection />

      {/* Contact Section */}
<section id="contacto" className="py-16 relative">
  <div className="max-w-4xl mx-auto px-6">
    <h2 className="text-3xl font-bold text-center mb-10 bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
      Conecta con Nosotros
    </h2>
    
    <div className="grid md:grid-cols-3 gap-6">
      <a
        href="https://wa.me/59168759357"
        target="_blank"
        rel="noopener noreferrer"
        className="group bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-center hover:scale-105 transition-transform shadow-lg"
      >
        <FaWhatsapp className="text-4xl mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h3 className="text-lg font-semibold mb-1">WhatsApp</h3>
        <p className="text-sm opacity-90">Haz tu pedido</p>
      </a>

      <a
        href="https://instagram.com/te_nawi"
        target="_blank"
        rel="noopener noreferrer"
        className="group bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl p-6 text-center hover:scale-105 transition-transform shadow-lg"
      >
        <FaInstagram className="text-4xl mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h3 className="text-lg font-semibold mb-1">Instagram</h3>
        <p className="text-sm opacity-90">@te_nawi</p>
      </a>

      <a
        href="https://www.tiktok.com/@te_nawi?_t=ZM-8yJJo3L2XWK&_r=1"
        target="_blank"
        rel="noopener noreferrer"
        className="group bg-gradient-to-r from-black to-gray-800 rounded-xl p-6 text-center hover:scale-105 transition-transform shadow-lg"
      >
        <FaTiktok className="text-4xl mx-auto mb-3 group-hover:scale-110 transition-transform" />
        <h3 className="text-lg font-semibold mb-1">TikTok</h3>
        <p className="text-sm opacity-90">@te_nawi</p>
      </a>
    </div>
  </div>
</section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex justify-center items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-amber-400 rounded-full flex items-center justify-center">
              <FaLeaf className="text-black text-sm" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-teal-400 to-amber-400 bg-clip-text text-transparent">
              Té Ñawi
            </span>
          </div>
          <p className="text-gray-400 mb-4">
            Honrando la sabiduría ancestral en cada sorbo
          </p>
          <p className="text-sm text-gray-500">
            © 2025 Té Ñawi. Todos los derechos reservados. Hecho con amor en Bolivia.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;